/* eslint-disable @typescript-eslint/camelcase */
import store from "@/store";
import { container } from "tsyringe";
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from "vuex-module-decorators";
import { CodReconcilePresenter } from "../presenters/CodReconcilePresenter";
import {
  CodDashboardDetailCourier,
  CourierDetail,
  CourierDetailApiRequest,
  CourierDetailList,
  CourierDetailPendingReconcile,
  CourierHistory,
  CourierNotes,
  CourierSummary,
  DashboardDeliveryCourierDetail,
  TaskSetllementCourier
} from "@/domain/entities/CodReconcile";
import { MainAppController } from "./MainAppController";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { ErrorMessageEntities } from "@/domain/entities/MainApp";
import {
  GetCourierNotes,
  SubmitCourierNotes,
  SubmitCourierNotesPhotos,
  TaskSettlementCourierParams
} from "@/data/payload/api/CodReconcileApiRequest";

@Module({
  namespaced: true,
  dynamic: true,
  store,
  name: "cod_reconcile"
})
class CodReconcileStore extends VuexModule {
  public isLoading = true;
  public courierSummary = new CourierSummary();
  public courierDetailList = new CourierDetailList();
  public courierDetailListPerStatus = new CourierDetailList();
  public courierDetailListSuccess: CourierDetail[] = [];
  public courierDetailListFailed: CourierDetail[] = [];
  public courierDetailListOnProgress: CourierDetail[] = [];
  public courierDetailSummary = new CodDashboardDetailCourier();
  public dashboardDeliveryCourierDetail = new DashboardDeliveryCourierDetail();
  public courierDetailStt: any = [];
  public previewImageDetailStt: any = [];

  @Action
  getCourierSummaryDetailV2(payload: {
    params: CourierDetailApiRequest;
    version: string;
  }): Promise<CourierSummary> {
    const presenter = container.resolve(CodReconcilePresenter);
    return presenter.getCourierSummaryDetailV2(payload.params, payload.version);
  }

  @Action
  getCourierDetail(payload: {
    params: CourierDetailApiRequest;
    version: string;
  }) {
    const presenter = container.resolve(CodReconcilePresenter);

    return new Promise((resolve, reject) => {
      this.setLoading(true);
      MainAppController.showLoading();
      presenter
        .getCourierDetail(payload.params, payload.version)
        .then((res) => {
          const newRes = new CourierDetailList({
            data: res.data.filter(
              (e) => e.sttCollectionStatus !== "Sudah Setor"
            )
          });
          this.setCourierDetail(newRes);
          resolve(res);
        })
        .catch((err) => {
          const message = err.response.data.message;
          MainAppController.showErrorMessage(
            typeof message === "string"
              ? new ErrorMessageEntities({
                  title: "Gagal Mengambil Detail Kurir",
                  message
                })
              : parsingErrorResponse(err)
          );
          reject(err);
        })
        .finally(() => {
          this.setLoading(false);
          MainAppController.closeLoading();
        });
    });
  }

  @Action
  getCourierDetailPerStatus(payload: {
    params: CourierDetailApiRequest;
    version: string;
  }) {
    const presenter = container.resolve(CodReconcilePresenter);

    return new Promise((resolve, reject) => {
      presenter
        .getCourierDetail(payload.params, payload.version)
        .then((res) => {
          if (payload.params.status === "success") {
            this.setCourierDetailListSuccess(res.data);
          }
          if (payload.params.status === "failed") {
            this.setCourierDetailListFailed(res.data);
          }
          if (payload.params.status === "onprogress") {
            this.setCourierDetailListOnProgress(res.data);
          }
          resolve(res);
        })
        .catch((err) => {
          const message = err.response.data.message;
          MainAppController.showErrorMessage(
            typeof message === "string"
              ? new ErrorMessageEntities({
                  title: "Gagal Mengambil Detail Kurir",
                  message
                })
              : parsingErrorResponse(err, "Gagal Mengambil Detail Kurir")
          );
          reject(err);
        });
    });
  }

  @Action
  getCourierDetailSummary(payload: CourierDetailApiRequest) {
    const presenter = container.resolve(CodReconcilePresenter);

    return new Promise((resolve, reject) => {
      this.setLoading(true);
      MainAppController.showLoading();
      presenter
        .getCourierDetailSummary(payload)
        .then((res) => {
          this.setCourierDetailSummary(res);
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          this.setLoading(false);
          MainAppController.closeLoading();
        });
    });
  }

  @Action
  getDashboardDeliveryCourierDetail(phone: string) {
    const presenter = container.resolve(CodReconcilePresenter);

    return new Promise((resolve, reject) => {
      presenter
        .getDashboardDeliveryCourierDetail(phone)
        .then((res) => {
          this.setDashboardDeliveryCourierDetail(res);
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  @Action
  getCourierDetailV2(payload: {
    params: CourierDetailApiRequest;
    version: string;
  }): Promise<CourierDetail[]> {
    const presenter = container.resolve(CodReconcilePresenter);
    return presenter.getCourierDetailV2(payload.params, payload.version);
  }

  @Action
  getDetailCourierPendingReconcile(payload: {
    params: CourierDetailApiRequest;
    version: string;
  }): Promise<CourierDetailPendingReconcile[]> {
    const presenter = container.resolve(CodReconcilePresenter);
    return presenter.getDetailCourierPendingReconcile(
      payload.params,
      payload.version
    );
  }

  @Action
  getCourierNotes(payload: GetCourierNotes): Promise<CourierNotes> {
    const presenter = container.resolve(CodReconcilePresenter);
    return presenter.getCourierNotes(payload);
  }

  @Action
  getCourierHistory(payload: GetCourierNotes): Promise<CourierHistory[]> {
    const presenter = container.resolve(CodReconcilePresenter);
    return presenter.getCourierHistory(payload);
  }

  @Action
  uploadCourierNotesPhotos(
    payload: SubmitCourierNotesPhotos
  ): Promise<boolean> {
    const presenter = container.resolve(CodReconcilePresenter);
    return presenter.uploadCourierNotesPhotos(payload);
  }

  @Action
  submitCourierNotes(payload: SubmitCourierNotes): Promise<boolean> {
    const presenter = container.resolve(CodReconcilePresenter);
    return presenter.submitCourierNotes(payload);
  }

  @Action
  getTaskSettlementCourier(
    params: TaskSettlementCourierParams
  ): Promise<TaskSetllementCourier[]> {
    const presenter = container.resolve(CodReconcilePresenter);
    return presenter.getTaskSettlementCourier(params);
  }

  @Action
  getTaskSettlementCourierDetail(id: number): Promise<TaskSetllementCourier> {
    const presenter = container.resolve(CodReconcilePresenter);
    return presenter.getTaskSettlementCourierDetail(id);
  }

  @Mutation
  setLoading(data: boolean) {
    this.isLoading = data;
  }

  @Mutation
  setCourierSummary(data: CourierSummary) {
    this.courierSummary = data;
  }

  @Mutation
  setCourierDetail(data: CourierDetailList) {
    this.courierDetailList = data;
  }

  @Mutation
  setCourierDetailListSuccess(data: CourierDetail[]) {
    this.courierDetailListSuccess = data;
  }

  @Mutation
  setCourierDetailListFailed(data: CourierDetail[]) {
    this.courierDetailListFailed = data;
  }

  @Mutation
  setCourierDetailListOnProgress(data: CourierDetail[]) {
    this.courierDetailListOnProgress = data;
  }

  @Mutation
  setCourierDetailSummary(data: CodDashboardDetailCourier) {
    this.courierDetailSummary = data;
  }

  @Mutation
  setDashboardDeliveryCourierDetail(data: DashboardDeliveryCourierDetail) {
    this.dashboardDeliveryCourierDetail = data;
  }

  @Mutation
  setCourierDetailStt(data: any) {
    this.courierDetailStt = data;
  }

  @Mutation
  setPreviewImageDetailStt(data: any) {
    this.previewImageDetailStt = data;
  }
}

export const CodReconcileController = getModule(CodReconcileStore);
